import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "../lib/api";
import { X, ArrowUp, ArrowDown, GripVertical, Play, Film, Download, ExternalLink, RefreshCw, Trash2, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

const fmtDur = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const fmtMB = (b) => `${((b || 0) / 1e6).toFixed(1)} MB`;

const STATUS = {
  queued: { label: "Antri", cls: "bg-[#A1A1AA]" },
  processing: { label: "Proses", cls: "bg-[#D97706]" },
  done: { label: "Selesai", cls: "bg-[#16A34A]" },
  error: { label: "Gagal", cls: "bg-[#DC2626]" },
};

// kualitas → perkiraan bitrate (Mbps @480p, dari uji video WA) & label
const QUALITY = [
  { key: "tinggi", label: "Tinggi", desc: "paling tajam, file lebih besar", mbps: 2.0 },
  { key: "seimbang", label: "Seimbang", desc: "disarankan — kecil tapi tetap jernih", mbps: 1.3 },
  { key: "hemat", label: "Hemat", desc: "paling kecil, utk kirim WA", mbps: 0.95 },
];
const WM_COLORS = ["#FFFFFF", "#FACC15", "#000000", "#DC2626", "#002FA7", "#16A34A", "#F97316"];
const WM_SIZES = [{ key: "kecil", label: "Kecil" }, { key: "sedang", label: "Sedang" }, { key: "besar", label: "Besar" }];
const isLight = (hex) => {
  const n = parseInt((hex || "#FFFFFF").slice(1), 16);
  return 0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 150;
};

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} disalin ✓`);
  } catch (_) {
    toast.error("Gagal menyalin — blok teksnya lalu salin manual");
  }
};

function CopyBtn({ text, label }) {
  return (
    <button type="button" onClick={() => copyText(text, label)} title={`Salin ${label.toLowerCase()}`}
      className="shrink-0 px-1.5 py-1 border border-[#E4E4E7] text-[10px] font-bold uppercase flex items-center gap-1 hover:bg-[#F4F4F5]">
      <Copy size={11} /> Salin
    </button>
  );
}

// Saran AI: judul / deskripsi / hashtag / tag YouTube (SEO) dari caption + watermark + data order
function SeoModal({ job, onClose, onSaved }) {
  const [notes, setNotes] = useState(job.seo?.notes || "");
  const [seo, setSeo] = useState(job.seo || null);
  const [desc, setDesc] = useState(job.seo?.description || "");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/video-merge/${job.id}/ai-seo`, { notes }, { timeout: 240000 });
      setSeo(data);
      setDesc(data.description || "");
      onSaved(data);
      toast.success("Saran AI siap ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat saran AI", { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const tagsText = (seo?.tags || []).join(", ");
  const hashText = (seo?.hashtags || []).join(" ");
  const allText = seo ? `${seo.titles?.[0] || ""}\n\n${desc}\n\nTags: ${tagsText}` : "";

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-2 md:p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white border border-[#E4E4E7] w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="video-seo-modal">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Sparkles size={15} className="text-[#7C3AED]" />
          <div className="font-bold text-sm flex-1">Saran YouTube (AI) — judul, deskripsi & tag</div>
          <button onClick={onClose} disabled={busy} className="p-1.5 hover:bg-[#F4F4F5] disabled:opacity-50"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
          <div>
            <div className="text-[10px] text-[#52525B] mb-1">
              AI memakai layanan & kota order, caption tiap klip, dan teks watermark. Nama/alamat/no. HP pelanggan <b>tidak</b> dikirim.
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 1000))} rows={2} disabled={busy}
              placeholder="Catatan / kata kunci tambahan (opsional) — mis. area Bekasi Timur, garansi 1 bulan, WA 0812-xxxx, channel: Deteksi Pipa Bocor"
              className="w-full px-2 py-1.5 border border-[#E4E4E7] outline-none focus:border-[#7C3AED] resize-y" data-testid="video-seo-notes" />
            <button onClick={generate} disabled={busy} data-testid="video-seo-generate"
              className="mt-1.5 px-3 py-2 bg-[#7C3AED] text-white font-bold uppercase text-[11px] flex items-center gap-1.5 hover:bg-[#6D28D9] disabled:opacity-60">
              <Sparkles size={13} /> {busy ? "AI sedang menyusun… (±20–60 detik)" : seo ? "Buat Ulang" : "Buat Saran"}
            </button>
          </div>

          {seo && (
            <>
              {seo.focus_keyword && <div className="text-[11px]">Kata kunci utama: <b className="text-[#7C3AED]">{seo.focus_keyword}</b></div>}

              <div>
                <div className="font-bold mb-1">Pilihan Judul</div>
                <div className="space-y-1.5">
                  {(seo.titles || []).map((t, i) => (
                    <div key={i} className="flex items-start gap-2 border border-[#E4E4E7] p-2" data-testid={`video-seo-title-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[13px] leading-snug break-words">{t}</div>
                        <div className={`text-[10px] font-mono mt-0.5 ${t.length > 100 ? "text-[#DC2626]" : t.length > 75 ? "text-[#D97706]" : "text-[#A1A1AA]"}`}>{t.length}/100 karakter</div>
                      </div>
                      <CopyBtn text={t} label="Judul" />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-bold flex-1">Deskripsi <span className="font-mono font-normal text-[10px] text-[#A1A1AA]">{desc.length}/5000</span></div>
                  <CopyBtn text={desc} label="Deskripsi" />
                </div>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={12}
                  className="w-full px-2 py-1.5 border border-[#E4E4E7] outline-none focus:border-[#7C3AED] resize-y leading-relaxed" data-testid="video-seo-description" />
                <div className="text-[10px] text-[#A1A1AA]">Bisa diedit sebelum disalin.</div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-bold flex-1">Hashtag</div>
                  <CopyBtn text={hashText} label="Hashtag" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(seo.hashtags || []).map((h) => <span key={h} className="px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#6D28D9] font-bold">{h}</span>)}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-bold flex-1">Tag YouTube <span className="font-mono font-normal text-[10px] text-[#A1A1AA]">{tagsText.length}/500 · {(seo.tags || []).length} tag</span></div>
                  <CopyBtn text={tagsText} label="Tag" />
                </div>
                <div className="border border-[#E4E4E7] bg-[#FAFAFA] p-2 leading-relaxed break-words" data-testid="video-seo-tags">{tagsText}</div>
                <div className="text-[10px] text-[#A1A1AA] mt-0.5">Tempel di kolom "Tag" YouTube Studio (dipisah koma).</div>
              </div>
            </>
          )}
        </div>
        {seo && (
          <div className="px-4 py-3 border-t border-[#E4E4E7] flex items-center gap-2">
            <div className="text-[10px] text-[#A1A1AA] flex-1">Dibuat oleh {seo.model}. Periksa lagi sebelum diunggah.</div>
            <button onClick={() => copyText(allText, "Judul + deskripsi + tag")} className="px-3 py-2 border border-[#E4E4E7] font-bold uppercase text-[11px] flex items-center gap-1 hover:bg-[#F4F4F5]">
              <Copy size={12} /> Salin Semua
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VideoMergeModal({ orderId, title, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [folder, setFolder] = useState(null); // {folder_name, folder_link}
  const [clips, setClips] = useState([]); // [{file_id, name, duration_ms, size, seq, caption, duplicate_of, has_thumb}] — urut nama file
  const [jobs, setJobs] = useState([]);
  const [preview, setPreview] = useState(null); // {src, name}
  const [submitting, setSubmitting] = useState(false);
  const [fonts, setFonts] = useState([]);
  const [wm, setWm] = useState({ enabled: false, text: "", font: "montserrat", color: "#FFFFFF", size: "sedang" });
  const [quality, setQuality] = useState("seimbang");
  const [delJob, setDelJob] = useState(null); // {job, drive, busy}
  const [seoFor, setSeoFor] = useState(null); // job yang sedang dibuatkan saran AI
  const dragIdx = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/orders/${orderId}/videos`);
      setFolder({ folder_name: data.folder_name, folder_link: data.folder_link });
      // seq = nomor urut di video hasil (0 = tidak dipakai); ditentukan oleh urutan klik centang
      setClips(data.videos.map((v) => ({ ...v, seq: 0, caption: "" })));
      setJobs(data.jobs || []);
      setFonts(data.fonts || []);
      if (data.defaults) {
        setWm((p) => ({ ...p, ...(data.defaults.watermark || {}) }));
        setQuality(data.defaults.quality || "seimbang");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat video dari Drive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // polling status proses
  const busy = jobs.some((j) => j.status === "queued" || j.status === "processing");
  useEffect(() => {
    if (!busy) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/video-merge`);
        setJobs(data || []);
        const done = (data || [])[0];
        if (done && done.status === "done" && jobs[0]?.status !== "done") toast.success("Video gabungan selesai ✓");
        if (done && done.status === "error" && jobs[0]?.status !== "error") toast.error("Gabung video gagal");
      } catch (_) {}
    }, 4000);
    return () => clearInterval(iv);
  }, [busy, orderId, jobs]);

  const selected = useMemo(() => clips.filter((c) => c.seq > 0).sort((a, b) => a.seq - b.seq), [clips]);
  const totalMs = selected.reduce((a, c) => a + (c.duration_ms || 0), 0);
  // tampilan daftar: "nama" = urut nama file (tetap, enak utk klik centang berurutan); "urutan" = sesuai nomor hasil
  const [view, setView] = useState("nama");
  const shown = useMemo(() => (view === "urutan" ? [...selected, ...clips.filter((c) => !c.seq)] : clips), [view, clips, selected]);

  // terapkan daftar file_id terurut sbg nomor 1..n, sisanya 0
  const applyOrder = (list, ids) => {
    const pos = new Map(ids.map((id, k) => [id, k + 1]));
    return list.map((c) => ({ ...c, seq: pos.get(c.file_id) || 0 }));
  };
  const orderedIds = (list) => list.filter((c) => c.seq > 0).sort((a, b) => a.seq - b.seq).map((c) => c.file_id);

  const updateClip = (fid, patch) => setClips((p) => p.map((c) => (c.file_id === fid ? { ...c, ...patch } : c)));
  const toggleClip = (fid, on) => setClips((p) => {
    const ids = orderedIds(p).filter((id) => id !== fid);
    if (on) ids.push(fid); // nomor berikutnya = urutan klik
    return applyOrder(p, ids);
  });
  const selectAll = (filterFn = () => true) => setClips((p) => {
    const ids = orderedIds(p).filter((id) => filterFn(p.find((c) => c.file_id === id)));
    p.forEach((c) => { if (!ids.includes(c.file_id) && filterFn(c)) ids.push(c.file_id); }); // sisanya urut nama
    return applyOrder(p, ids);
  });
  const clearAll = () => setClips((p) => p.map((c) => ({ ...c, seq: 0 })));
  // geser nomor urut sebuah klip (▲▼) / pindahkan ke posisi klip lain (seret)
  const moveSeq = (fid, toIndex) => setClips((p) => {
    const ids = orderedIds(p);
    const from = ids.indexOf(fid);
    if (from < 0 || toIndex < 0 || toIndex >= ids.length || from === toIndex) return p;
    ids.splice(from, 1);
    ids.splice(toIndex, 0, fid);
    return applyOrder(p, ids);
  });
  const sortByName = () => setClips((p) => applyOrder(p, [...orderedIds(p)].sort((a, b) => {
    const na = p.find((c) => c.file_id === a).name, nb = p.find((c) => c.file_id === b).name;
    return na.localeCompare(nb, "id", { numeric: true });
  })));

  const submit = async () => {
    if (!selected.length) { toast.error("Centang minimal 1 video"); return; }
    setSubmitting(true);
    try {
      if (wm.enabled && !wm.text.trim()) { toast.error("Isi teks watermark, atau matikan watermark"); setSubmitting(false); return; }
      const { data } = await api.post(`/orders/${orderId}/video-merge`, {
        clips: selected.map((c) => ({ file_id: c.file_id, caption: c.caption || "" })),
        watermark: wm.enabled ? { text: wm.text, font: wm.font, color: wm.color, size: wm.size } : null,
        quality,
      });
      setJobs((p) => [data, ...p]);
      toast.success("Proses gabung video dimulai — bisa ditinggal, hasil muncul di bawah.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai proses", { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const streamUrl = (fid) => `${API}/orders/${orderId}/videos/${fid}/stream`;

  const confirmDelete = async () => {
    if (!delJob || delJob.busy) return;
    setDelJob((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.delete(`/video-merge/${delJob.job.id}`, { params: { drive: delJob.drive } });
      setJobs((p) => p.filter((j) => j.id !== delJob.job.id));
      if (preview?.src?.includes(delJob.job.id)) setPreview(null);
      toast.success(data.drive_trashed ? "Video gabungan dihapus (file Drive dipindah ke Sampah)" : "Video gabungan dihapus");
      setDelJob(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus", { duration: 8000 });
      setDelJob((d) => (d ? { ...d, busy: false } : d));
    }
  };

  const qInfo = QUALITY.find((q) => q.key === quality) || QUALITY[1];
  const estMB = (qInfo.mbps * totalMs) / 1000 / 8;
  const estMin = Math.max(1, Math.ceil(totalMs / 60000 / 1.4 + selected.length * 0.1));
  const fontFamily = (key) => `vm-${key}, sans-serif`;
  const previewFs = { kecil: 13, sedang: 17, besar: 22 }[wm.size] || 17;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 md:p-4" data-testid="video-merge-modal">
      <div className="bg-white border border-[#E4E4E7] w-full max-w-5xl max-h-[95vh] md:h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center gap-2">
          <Film size={16} className="text-[#002FA7]" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">Gabung Video{title ? ` — ${title}` : ""}</div>
            {folder && (
              <a href={folder.folder_link} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline font-mono truncate block">
                📁 {folder.folder_name || "Folder order"} ↗
              </a>
            )}
          </div>
          <button onClick={load} disabled={loading} title="Muat ulang daftar video" className="p-1.5 hover:bg-[#F4F4F5] disabled:opacity-50"><RefreshCw size={14} /></button>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F4F5]" data-testid="video-merge-close"><X size={16} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden grid md:grid-cols-[1fr_300px] md:grid-rows-[minmax(0,1fr)]">
          {/* daftar klip */}
          <div className="p-3 border-r border-[#E4E4E7] min-w-0 md:min-h-0 md:overflow-y-auto" data-testid="video-merge-list">
            {loading ? (
              <div className="text-center text-xs text-[#52525B] py-10">Memuat video dari Drive…</div>
            ) : err ? (
              <div className="text-xs text-[#DC2626] py-6 text-center">{err}</div>
            ) : clips.length === 0 ? (
              <div className="text-xs text-[#52525B] py-6 text-center">Tidak ada video di folder ini.</div>
            ) : (
              <>
                <div className="sticky top-0 z-10 bg-white -mt-3 pt-3 pb-2 mb-1 border-b border-[#F4F4F5]" data-testid="video-merge-select-bar">
                  <div className="flex items-center gap-3 flex-wrap text-xs">
                    <label className="flex items-center gap-2 font-bold cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selected.length === clips.length}
                        ref={(el) => { if (el) el.indeterminate = selected.length > 0 && selected.length < clips.length; }}
                        onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
                        data-testid="video-merge-select-all"
                      />
                      Pilih semua ({selected.length}/{clips.length})
                    </label>
                    <button type="button" onClick={clearAll} disabled={!selected.length}
                      className="font-bold text-[#002FA7] hover:underline disabled:opacity-40 disabled:no-underline" data-testid="video-merge-unselect-all">
                      Kosongkan pilihan
                    </button>
                    {clips.some((c) => c.duplicate_of) && (
                      <button type="button" onClick={() => selectAll((c) => !c.duplicate_of)}
                        className="font-bold text-[#D97706] hover:underline" data-testid="video-merge-select-nodup">
                        Pilih semua kecuali duplikat
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-2 text-[11px]">
                    <span className="text-[#52525B]">Tampilkan:</span>
                    <div className="inline-flex border border-[#E4E4E7]" data-testid="video-merge-view">
                      {[{ k: "nama", l: "Nama file" }, { k: "urutan", l: "Urutan hasil" }].map((o) => (
                        <button key={o.k} type="button" onClick={() => setView(o.k)}
                          className={`px-2 py-1 font-bold ${view === o.k ? "bg-[#002FA7] text-white" : "bg-white hover:bg-[#F4F4F5]"}`}
                          data-testid={`video-merge-view-${o.k}`}>{o.l}</button>
                      ))}
                    </div>
                    {selected.length > 1 && (
                      <button type="button" onClick={sortByName} className="font-bold text-[#002FA7] hover:underline" title="Atur ulang nomor urut sesuai nama file" data-testid="video-merge-renumber-name">
                        Nomori ulang sesuai nama
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] text-[#52525B] mt-1">
                    Klik centang sesuai urutan yang diinginkan — nomor biru = urutan di video hasil. Ubah urutan dengan ▲▼ atau seret (⋮⋮) ke klip lain. Klik thumbnail untuk memutar.
                  </div>
                </div>
                <div className="space-y-2">
                  {shown.map((c, i) => {
                    const order = c.seq || null;
                    const on = c.seq > 0;
                    return (
                      <div
                        key={c.file_id}
                        draggable={on}
                        onDragStart={() => { dragIdx.current = c.file_id; }}
                        onDragOver={(e) => { if (on) e.preventDefault(); }}
                        onDrop={() => { if (dragIdx.current && on) moveSeq(dragIdx.current, c.seq - 1); dragIdx.current = null; }}
                        className={`flex gap-2 border p-2 ${on ? "border-[#E4E4E7] bg-white" : "border-dashed border-[#E4E4E7] bg-[#FAFAFA] opacity-60"}`}
                        data-testid={`video-merge-clip-${i}`}
                      >
                        <div className={`flex flex-col items-center gap-1 pt-1 text-[#A1A1AA] ${on ? "" : "invisible"}`}>
                          <GripVertical size={14} className="cursor-grab" />
                          <button onClick={() => moveSeq(c.file_id, c.seq - 2)} disabled={!on || c.seq === 1} className="hover:text-[#002FA7] disabled:opacity-30" title="Majukan urutan"><ArrowUp size={13} /></button>
                          <button onClick={() => moveSeq(c.file_id, c.seq)} disabled={!on || c.seq === selected.length} className="hover:text-[#002FA7] disabled:opacity-30" title="Mundurkan urutan"><ArrowDown size={13} /></button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPreview({ src: streamUrl(c.file_id), name: c.name })}
                          className="relative w-16 h-24 shrink-0 bg-[#18181B] flex items-center justify-center overflow-hidden"
                          title="Putar"
                        >
                          {c.has_thumb && <img src={`${API}/orders/${orderId}/videos/${c.file_id}/thumb`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                          <Play size={18} className="relative text-white drop-shadow" />
                          {order && <span className="absolute top-0 left-0 bg-[#002FA7] text-white text-[10px] font-bold px-1">{order}</span>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" className="mt-0.5" checked={on} onChange={(e) => toggleClip(c.file_id, e.target.checked)} data-testid={`video-merge-include-${i}`} />
                            <span className="text-xs font-bold break-all">{c.name}</span>
                          </label>
                          <div className="text-[10px] text-[#A1A1AA] font-mono mt-0.5">{fmtDur(c.duration_ms)} · {fmtMB(c.size)}</div>
                          {c.duplicate_of && (
                            <div className="text-[10px] text-[#D97706] font-bold mt-0.5">⚠ Kemungkinan sama dengan "{c.duplicate_of}" (durasi & ukuran identik)</div>
                          )}
                          <textarea
                            value={c.caption}
                            onChange={(e) => updateClip(c.file_id, { caption: e.target.value.slice(0, 200) })}
                            disabled={!on}
                            rows={1}
                            placeholder="Caption (opsional) — tampil di bawah video selama klip ini"
                            className="mt-1 w-full px-2 py-1 border border-[#E4E4E7] text-xs outline-none focus:border-[#002FA7] resize-y disabled:bg-transparent"
                            data-testid={`video-merge-caption-${i}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* pengaturan + pratinjau + hasil */}
          <div className="p-3 pt-0 flex flex-col gap-3 min-w-0 md:min-h-0 md:overflow-y-auto">
            <style>{fonts.map((f) => `@font-face{font-family:'vm-${f.key}';src:url('${API}/video-merge/fonts/${f.key}') format('truetype');font-display:swap;}`).join("")}</style>

            {/* pratinjau tetap terlihat (sticky) saat panel kanan di-scroll */}
            <div className="md:sticky md:top-0 z-10 bg-white pt-3 pb-1" data-testid="video-merge-preview">
              <div className="label-cap text-[10px] mb-1">Pratinjau</div>
              {preview ? (
                <>
                  <video key={preview.src} src={preview.src} controls autoPlay playsInline className="w-full max-h-[40vh] bg-black" />
                  <div className="text-[10px] text-[#52525B] mt-1 break-all line-clamp-1">{preview.name}</div>
                </>
              ) : (
                <div className="h-[30vh] w-full bg-[#F4F4F5] flex items-center justify-center text-[10px] text-[#A1A1AA] text-center px-4">Klik thumbnail video untuk memutar</div>
              )}
            </div>

            <div className="border border-[#E4E4E7] p-2.5" data-testid="video-merge-settings">
              <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                <input type="checkbox" checked={wm.enabled} onChange={(e) => setWm((p) => ({ ...p, enabled: e.target.checked }))} data-testid="video-merge-wm-enable" />
                Watermark
              </label>
              {wm.enabled && (
                <div className="mt-2 space-y-2">
                  <input
                    value={wm.text}
                    onChange={(e) => setWm((p) => ({ ...p, text: e.target.value.slice(0, 80) }))}
                    placeholder="Teks watermark, mis. nama usaha / no. WA"
                    className="w-full px-2 py-1.5 border border-[#E4E4E7] text-xs outline-none focus:border-[#002FA7]"
                    data-testid="video-merge-wm-text"
                  />
                  <div className="flex gap-2">
                    <select value={wm.font} onChange={(e) => setWm((p) => ({ ...p, font: e.target.value }))}
                      className="flex-1 min-w-0 px-1.5 py-1.5 border border-[#E4E4E7] text-xs bg-white" style={{ fontFamily: fontFamily(wm.font) }} data-testid="video-merge-wm-font">
                      {fonts.map((f) => <option key={f.key} value={f.key} style={{ fontFamily: fontFamily(f.key) }}>{f.label}</option>)}
                    </select>
                    <select value={wm.size} onChange={(e) => setWm((p) => ({ ...p, size: e.target.value }))}
                      className="px-1.5 py-1.5 border border-[#E4E4E7] text-xs bg-white" data-testid="video-merge-wm-size">
                      {WM_SIZES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {WM_COLORS.map((c) => (
                      <button key={c} type="button" title={c} onClick={() => setWm((p) => ({ ...p, color: c }))}
                        className={`w-6 h-6 border ${wm.color.toUpperCase() === c ? "ring-2 ring-[#002FA7] ring-offset-1" : "border-[#A1A1AA]"}`}
                        style={{ background: c }} />
                    ))}
                    <label className="relative w-6 h-6 border border-[#A1A1AA] cursor-pointer overflow-hidden" title="Warna lain"
                      style={{ background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)" }}>
                      <input type="color" value={wm.color} onChange={(e) => setWm((p) => ({ ...p, color: e.target.value.toUpperCase() }))}
                        className="absolute inset-0 opacity-0 cursor-pointer" data-testid="video-merge-wm-color" />
                    </label>
                    <span className="text-[10px] font-mono text-[#52525B]">{wm.color.toUpperCase()}</span>
                  </div>
                  {/* contoh tampilan di latar terang & gelap */}
                  <div className="grid grid-cols-2 text-center">
                    {["#E4E4E7", "#27272A"].map((bg) => (
                      <div key={bg} className="h-14 flex items-end justify-center pb-1.5 px-1 overflow-hidden" style={{ background: bg }}>
                        <span className="font-bold leading-tight break-words" style={{
                          fontFamily: fontFamily(wm.font), color: wm.color, fontSize: previewFs,
                          WebkitTextStroke: `${Math.max(0.6, previewFs / 18)}px ${isLight(wm.color) ? "rgba(0,0,0,.85)" : "rgba(255,255,255,.9)"}`,
                          paintOrder: "stroke fill", textShadow: "1px 1px 2px rgba(0,0,0,.45)",
                        }}>{wm.text || "Contoh watermark"}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-[#A1A1AA]">Tampil di bagian paling bawah seluruh video. Garis tepi kontras ditambahkan otomatis agar tetap terbaca.</div>
                </div>
              )}

              <div className="mt-3 text-xs font-bold">Kualitas / Kompresi</div>
              <div className="mt-1 space-y-1">
                {QUALITY.map((q) => (
                  <label key={q.key} className="flex items-start gap-2 text-xs cursor-pointer">
                    <input type="radio" name="vm-quality" className="mt-0.5" checked={quality === q.key} onChange={() => setQuality(q.key)} data-testid={`video-merge-quality-${q.key}`} />
                    <span><b>{q.label}</b> <span className="text-[#52525B]">— {q.desc}</span></span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="label-cap text-[10px] mb-1">Hasil Gabungan</div>
              {jobs.length === 0 ? (
                <div className="text-[10px] text-[#A1A1AA]">Belum ada.</div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((j) => {
                    const st = STATUS[j.status] || STATUS.queued;
                    const fileUrl = `${API}/video-merge/${j.id}/file`;
                    return (
                      <div key={j.id} className="border border-[#E4E4E7] p-2 text-[11px]" data-testid={`video-merge-job-${j.id}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-white text-[9px] font-bold uppercase px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                          <span className="text-[#52525B] truncate flex-1">
                            {j.clips?.length} klip{j.duration_sec ? ` · ${fmtDur(j.duration_sec * 1000)}` : ""}{j.output_size ? ` · ${fmtMB(j.output_size)}` : ""}
                          </span>
                          {j.status !== "processing" && (
                            <button onClick={() => setDelJob({ job: j, drive: !!j.drive_file_id, busy: false })} title="Hapus video gabungan"
                              className="p-1 text-[#DC2626] hover:bg-[#DC2626]/10" data-testid={`video-merge-job-delete-${j.id}`}>
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div className="text-[10px] text-[#A1A1AA] mt-0.5">
                          {(QUALITY.find((q) => q.key === j.quality) || {}).label || "—"}{j.watermark?.text ? ` · WM: ${j.watermark.text}` : ""}
                        </div>
                        {(j.status === "queued" || j.status === "processing") && <div className="text-[#D97706] mt-1">{j.progress}</div>}
                        {j.status === "error" && <div className="text-[#DC2626] mt-1 break-words">{j.error}</div>}
                        {j.status === "done" && (
                          <>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                              <button onClick={() => setPreview({ src: fileUrl, name: j.output_name })} className="font-bold text-[#002FA7] hover:underline flex items-center gap-1"><Play size={11} /> Putar</button>
                              <a href={fileUrl} download={j.output_name} className="font-bold text-[#002FA7] hover:underline flex items-center gap-1"><Download size={11} /> Unduh</a>
                              {j.drive_link && <a href={j.drive_link} target="_blank" rel="noreferrer" className="font-bold text-[#002FA7] hover:underline flex items-center gap-1"><ExternalLink size={11} /> Drive</a>}
                            </div>
                            {j.drive_note && <div className="text-[10px] text-[#A1A1AA] mt-1">{j.drive_note}</div>}
                            <button onClick={() => setSeoFor(j)} data-testid={`video-merge-seo-${j.id}`}
                              className="mt-1.5 w-full px-2 py-1.5 border border-[#7C3AED]/40 bg-[#7C3AED]/5 text-[#6D28D9] font-bold text-[10px] uppercase flex items-center justify-center gap-1 hover:bg-[#7C3AED]/10">
                              <Sparkles size={11} /> {j.seo ? "Lihat Saran YouTube (AI)" : "Saran YouTube (AI)"}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-[#E4E4E7] flex items-center gap-3 flex-wrap">
          <div className="text-xs text-[#52525B] flex-1">
            <b>{selected.length}</b> dari {clips.length} video dipakai · total <b>{fmtDur(totalMs)}</b>
            {selected.length > 0 && <> · perkiraan file <b>±{estMB < 1 ? estMB.toFixed(1) : Math.round(estMB)} MB</b></>}
            <span className="text-[10px] text-[#A1A1AA] block">Perkiraan proses ±{estMin} menit. Hasil disimpan di subfolder "Gabungan" (tidak ikut dihitung bonus video).</span>
          </div>
          <button onClick={onClose} className="px-3 py-2 border border-[#E4E4E7] text-xs font-bold uppercase hover:bg-[#F4F4F5]">Tutup</button>
          <button
            onClick={submit}
            disabled={submitting || busy || !selected.length || loading}
            className="px-4 py-2 bg-[#002FA7] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#00268A] disabled:opacity-50"
            data-testid="video-merge-submit"
          >
            <Film size={13} /> {busy ? "Sedang diproses…" : submitting ? "Memulai…" : "Gabungkan"}
          </button>
        </div>
      </div>

      {seoFor && (
        <SeoModal
          job={seoFor}
          onClose={() => setSeoFor(null)}
          onSaved={(seo) => setJobs((p) => p.map((x) => (x.id === seoFor.id ? { ...x, seo } : x)))}
        />
      )}

      {delJob && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => !delJob.busy && setDelJob(null)}>
          <div className="bg-white border border-[#E4E4E7] w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="video-merge-delete-modal">
            <div className="font-bold text-sm mb-2 flex items-center gap-2 text-[#DC2626]"><Trash2 size={15} /> Hapus video gabungan?</div>
            <div className="text-xs text-[#52525B] mb-3 break-words">
              <b>{delJob.job.output_name || `${delJob.job.clips?.length || 0} klip`}</b> akan dihapus dari aplikasi (file di server + riwayat).
              Video asli di folder order tidak tersentuh.
            </div>
            {delJob.job.drive_file_id && (
              <label className="flex items-start gap-2 text-xs mb-4 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={delJob.drive} disabled={delJob.busy}
                  onChange={(e) => setDelJob((d) => ({ ...d, drive: e.target.checked }))} data-testid="video-merge-delete-drive" />
                <span>Hapus juga file di Google Drive <span className="text-[#A1A1AA]">(dipindah ke Sampah Drive, masih bisa dipulihkan ±30 hari)</span></span>
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDelJob(null)} disabled={delJob.busy} className="px-3 py-2 border border-[#E4E4E7] text-xs font-bold uppercase hover:bg-[#F4F4F5] disabled:opacity-50">Batal</button>
              <button onClick={confirmDelete} disabled={delJob.busy} className="px-3 py-2 bg-[#DC2626] text-white text-xs font-bold uppercase hover:bg-[#B91C1C] disabled:opacity-50" data-testid="video-merge-delete-confirm">
                {delJob.busy ? "Menghapus…" : "Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
